import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { Log, LogStateTypes } from "./Log.util";

const name = "log";
const extraActions = createExtraActions();

const LogInitialState: LogStateTypes = {
  logs: ResetApiState([]),
  pagedLogs: ResetApiState(ResetPaginationState()),
  logCount: ResetApiState(null),
  updateLogSeen: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const getLogs = createAsyncThunk<Log[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedLogs = createAsyncThunk<PaginationType<Log[]>, any>(
    `/get-paged-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}/paged${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getLogCount = createAsyncThunk<number, any>(
    `/get-${name}-count`,
    async (_: any, thunkApi) =>
      axios
        .get(`${name}/unseen-count`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const updateLogSeen = createAsyncThunk<ResponseWrapper<any>, any>(
    `/update-last-seen-${name}s`,
    async (lastSeen: any, thunkApi) => {
      return axios
        .put(`/${name}/last-seen`, {
          lastSeen,
        })
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const resetStates = createAction(`/reset-states`);

  return {
    getLogs,
    getPagedLogs,
    getLogCount,
    updateLogSeen,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(builder: ActionReducerMapBuilder<LogStateTypes>) {
  return {
    ...logsReducer(),
    ...pagedLogsReducer(),
    ...logCountReducer(),
    ...updateLogSeenReducer(),
    ...resetStatesReducer(),
  };

  function logsReducer() {
    return {
      ...builder.addCase(extraActions.getLogs.pending, (state, action) => {
        state.logs = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getLogs.fulfilled, (state, action) => {
        state.logs = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getLogs.rejected, (state, action) => {
        state.logs = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function pagedLogsReducer() {
    return {
      ...builder.addCase(extraActions.getPagedLogs.pending, (state, action) => {
        state.pagedLogs = {
          loading: true,
          payload: ResetPaginationState(),
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getPagedLogs.fulfilled,
        (state, action) => {
          state.pagedLogs = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedLogs.rejected,
        (state, action) => {
          state.pagedLogs = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function logCountReducer() {
    return {
      ...builder.addCase(extraActions.getLogCount.pending, (state, action) => {
        state.logCount = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getLogCount.fulfilled,
        (state, action) => {
          state.logCount = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(extraActions.getLogCount.rejected, (state, action) => {
        state.logCount = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function updateLogSeenReducer() {
    return {
      ...builder.addCase(
        extraActions.updateLogSeen.pending,
        (state, action) => {
          state.updateLogSeen = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.updateLogSeen.fulfilled,
        (state, action) => {
          state.updateLogSeen = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.updateLogSeen.rejected,
        (state, action) => {
          state.updateLogSeen = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.logs = ResetApiState([]);
        state.pagedLogs = ResetApiState(ResetPaginationState());
        state.logCount = ResetApiState(null);
      }),
    };
  }
}

const LogSlice = createSlice({
  name,
  initialState: LogInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const LogActions = {
  ...LogSlice.actions,
  ...extraActions,
};
export const LogReducer = LogSlice.reducer;
